// Shared vocabulary for the marketplace (CDC) feed rules view.
//
// A marketplace store's config is a column mapping plus an ordered chain of
// transform steps. The API returns both in one envelope from
// GET /merchant/admin/external-feed/rules/{store_id}.

export const RULE_TYPE_COLUMN_MAPPING = 'column_mapping';
export const RULE_TYPE_TRANSFORM_STEP = 'transform_step';

// The nine canonical inventory columns a mapping can name, in display order.
// `required` mirrors the API: a mapping missing sku or price is rejected on save
// and re-extracted from Glue by the pipeline, discarding the edit.
export const MAPPING_FIELDS = [
  {
    key: 'sku',
    label: 'SKU',
    required: true,
    help: 'Header holding the retailer’s own product identifier. Rows are matched to Saucey products by this.',
  },
  {
    key: 'price',
    label: 'Price',
    required: true,
    help: 'Header holding the unit price.',
  },
  {
    key: 'quantity',
    label: 'Quantity',
    help: 'Header holding on-hand count. Unset means every row imports with quantity 0.',
  },
  {
    key: 'upc',
    label: 'UPC',
    help: 'Header holding the barcode. Unset means rows can only be matched by retailer SKU.',
  },
  { key: 'title', label: 'Title', help: 'Header holding the product name.' },
  { key: 'category', label: 'Category', help: 'Header holding the retailer’s category.' },
  { key: 'last_sold', label: 'Last sold', help: 'Header holding the last-sold date.' },
  {
    key: 'discontinued',
    label: 'Discontinued',
    help: 'Header flagging discontinued products.',
  },
];

export const REQUIRED_MAPPING_FIELDS = MAPPING_FIELDS.filter((f) => f.required).map((f) => f.key);

// Delimiters the CDC parser understands. Blank is a real, common state: it means
// "auto-detect from the header", which is how many production rows are stored.
export const DELIMITER_OPTIONS = [
  { value: '', label: 'Auto-detect from header' },
  { value: ',', label: 'Comma  ,' },
  { value: '|', label: 'Pipe  |' },
  { value: '\t', label: 'Tab' },
  { value: ';', label: 'Semicolon  ;' },
];

export const describeDelimiter = (value) => {
  if (value === undefined || value === null || value === '') return 'Auto-detect';
  if (value === '\t') return 'Tab';
  const known = { ',': 'Comma  ,', '|': 'Pipe  |', ';': 'Semicolon  ;' };
  return known[value] || value;
};

// Where a mapping or step came from. Glue rows are imports that nobody has
// confirmed yet, which is exactly what the audit view is for.
export const SOURCE_META = {
  glue: {
    label: 'Glue import',
    color: 'yellow',
    help: 'Extracted from the store’s legacy AWS Glue script. Save it to pin the mapping as manual.',
  },
  manual: {
    label: 'Manual',
    color: 'green',
    help: 'Authored here. Wins over Glue extraction.',
  },
  api: {
    label: 'Import settings API',
    color: 'blue',
    help: 'Came from the import-settings API and is not persisted by the pipeline.',
  },
  // Provenance markers the pipeline writes when Glue extraction produced nothing
  // usable. The ?source= filter only accepts glue|api|manual, so these cannot be
  // filtered on server-side yet, but they do come back on the row.
  'glue-none': {
    label: 'Glue: nothing found',
    color: 'red',
    help: 'Glue extraction ran and found no usable columns. The pipeline cannot ingest this store.',
  },
  'glue-unparsed': {
    label: 'Glue: unparsed',
    color: 'red',
    help: 'The store’s Glue script could not be parsed, so nothing was extracted from it.',
  },
};

export const sourceMeta = (source) =>
  SOURCE_META[source] || { label: source || 'Unknown', color: 'dark', help: '' };

export const EXECUTION_PATH_META = {
  steps: {
    label: 'Transform steps',
    color: 'green'
  },
  mapping: {
    label: 'Column mapping only',
    color: 'yellow'
  },
};

// For a multi-file store the mapping fallback is not a degraded version of the
// chain — it reads one upload and cannot join the others, so it stages the
// triggering file's rows alone. That is a different dataset, and calling it
// "lossy" understates it.
const MULTI_FILE_MAPPING_META = {
  label: 'Column mapping only — wrong dataset',
  color: 'red',
  blurb:
    'This store’s feed is assembled from several files. The mapping path reads only the triggering upload and cannot join the others, so it stages a different dataset — not a degraded one.',
};

export const executionPathMeta = (path, { multiFile = false } = {}) => {
  if (path === 'mapping' && multiFile) return MULTI_FILE_MAPPING_META;
  return EXECUTION_PATH_META[path] || { label: path || 'Unknown', color: 'dark', blurb: '' };
};

// Steps must be plain identifiers — the pipeline interpolates the name into a
// CREATE TABLE statement — and `source` is reserved for the raw parsed CSV.
export const STEP_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;
export const RESERVED_STEP_NAME = 'source';
export const MAX_STEP_SQL_LENGTH = 200000;

/**
 * Mirror of the API's step validation, so the form fails before the request does.
 * @returns {string|null} an error message, or null when valid
 */
export const validateStepDraft = (draft, existingSteps = [], editingId = null, sources = []) => {
  const name = (draft.name || '').trim();
  if (!name) return 'Name is required — later steps select from this step by name.';
  if (!STEP_NAME_PATTERN.test(name)) {
    return `Name "${name}" must be a plain identifier ([A-Za-z_][A-Za-z0-9_]*): the pipeline interpolates it into a CREATE TABLE statement.`;
  }
  if (name.toLowerCase() === RESERVED_STEP_NAME) {
    return `Name "${RESERVED_STEP_NAME}" is reserved: it is the table holding the raw parsed CSV.`;
  }
  const clash = existingSteps.find(
    (s) => s.id !== editingId && (s.name || '').toLowerCase() === name.toLowerCase()
  );
  if (clash) return `This store already has a step named "${clash.name}" (order ${clash.stepOrder}).`;

  // Mirror of the input-side check: the executor sees one table for two names
  // differing only in case, so the step would shadow the declared input.
  const inputClash = sources.find((s) => (s.name || '').toLowerCase() === name.toLowerCase());
  if (inputClash) {
    return `Name "${name}" collides with declared input "${inputClash.name}". SQLite identifiers are case-insensitive, so this step would shadow that input.`;
  }

  const sql = (draft.sql || '').trim();
  if (!sql) return 'SQL is required.';
  if (sql.length > MAX_STEP_SQL_LENGTH) {
    return `SQL must be ${MAX_STEP_SQL_LENGTH.toLocaleString()} characters or fewer.`;
  }
  const body = sql.replace(/;\s*$/, '').trim();
  if (containsStatementSeparator(body)) {
    return 'SQL must be a single statement — the executor wraps it as CREATE TABLE <name> AS <sql>.';
  }
  // Judge the statement by its first real keyword. Imported steps routinely open
  // with a `-- …` header, and a leading comment doesn't change what the
  // statement is.
  const upper = stripLeadingComments(body).toUpperCase();
  if (!upper.startsWith('SELECT') && !upper.startsWith('WITH')) {
    return 'SQL must start with SELECT or WITH so the step produces rows.';
  }
  return null;
};

/** Drop any run of leading line (`--`) and block (`/* *\/`) comments. */
function stripLeadingComments(sql) {
  let out = String(sql || '').trim();
  for (;;) {
    if (out.startsWith('--')) {
      const nl = out.indexOf('\n');
      if (nl === -1) return ''; // nothing but a comment
      out = out.slice(nl + 1).trim();
    } else if (out.startsWith('/*')) {
      const end = out.indexOf('*/');
      if (end === -1) return ''; // unterminated block comment
      out = out.slice(end + 2).trim();
    } else {
      return out;
    }
  }
}

// A bare scan for ';' would reject legitimate SQL such as replace(x, ';', ''),
// and would also trip on a semicolon inside a comment, which is not a separator.
function containsStatementSeparator(sql) {
  const text = String(sql || '');
  let inSingle = false;
  let inDouble = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];

    if (!inSingle && !inDouble) {
      if (ch === '-' && next === '-') {
        const nl = text.indexOf('\n', i);
        if (nl === -1) break;
        i = nl;
        continue;
      }
      if (ch === '/' && next === '*') {
        const end = text.indexOf('*/', i + 2);
        if (end === -1) break;
        i = end + 1;
        continue;
      }
    }

    if (ch === "'" && !inDouble) inSingle = !inSingle;
    else if (ch === '"' && !inSingle) inDouble = !inDouble;
    else if (ch === ';' && !inSingle && !inDouble) return true;
  }
  return false;
}

/**
 * Mirror of the API's declared-input validation.
 *
 * Name collisions are checked case-insensitively against both other inputs and
 * transform steps: Postgres's UNIQUE (store_id, name) is case-sensitive so
 * LIQCODE and liqcode can both be stored, but SQLite identifiers are not, and
 * the executor would see one table.
 *
 * @returns {string|null} an error message, or null when valid
 */
export const validateSourceDraft = (draft, sources = [], steps = [], editingId = null) => {
  const name = (draft.name || '').trim();
  if (!name) return 'Name is required — it is the table name the step SQL selects from.';
  if (!STEP_NAME_PATTERN.test(name)) {
    return `Name "${name}" must be a plain identifier ([A-Za-z_][A-Za-z0-9_]*): it is interpolated into SQL as a table name.`;
  }
  if (name.toLowerCase() === RESERVED_STEP_NAME) {
    return `Name "${RESERVED_STEP_NAME}" is reserved: it is the table a single-file store's upload is registered as.`;
  }

  const dupInput = sources.find(
    (s) => s.id !== editingId && (s.name || '').toLowerCase() === name.toLowerCase()
  );
  if (dupInput) return `This store already declares an input named "${dupInput.name}".`;

  const stepClash = steps.find((s) => (s.name || '').toLowerCase() === name.toLowerCase());
  if (stepClash) {
    return `Name "${name}" collides with transform step "${stepClash.name}". SQLite identifiers are case-insensitive, so the step would shadow this input.`;
  }

  const shared = !!draft.shared;
  const fileName = (draft.fileName || '').trim();
  if (!fileName) return 'File name is required.';
  if (fileName.includes('..')) return 'File name may not contain "..".';
  if (fileName.startsWith('/')) {
    return 'File name may not start with "/" — keys are relative to the feed bucket.';
  }
  if (!shared && fileName.includes('/')) {
    return 'For a store input this is the suffix its uploads end in (e.g. "liqcode.csv"), not a path. Tick “shared” for a file fetched from its own fixed key.';
  }

  if (draft.isTrigger && shared) {
    return 'A shared input cannot be the trigger: it is not part of this store’s upload, so its arrival is not this store’s cycle.';
  }

  const pattern = (draft.pattern || '').trim();
  if (pattern) {
    // The pipeline compiles `(?i)^(?:<pattern>)$` with Go's RE2. JS regexp is a
    // near-enough check to catch a typo here; RE2-specific rejections still come
    // back from the API.
    try {
      new RegExp(`^(?:${pattern})$`, 'i');
    } catch (err) {
      return `Pattern is not a valid regexp: ${err.message}`;
    }
  }

  return null;
};

/**
 * Split the API's flat `rules` envelope into the shapes this UI renders.
 *
 * Steps are read from `rules` rather than `transform_steps` because only the
 * rule envelope carries the step's numeric id, which every write needs. Declared
 * inputs are the other way round: they are deliberately absent from `rules` (an
 * input declaration is not a transformation rule) and carry their own id, so
 * they come off `transform_sources` directly.
 */
export const normalizeStoreRules = (store) => {
  const rules = Array.isArray(store?.rules) ? store.rules : [];

  const steps = rules
    .filter((r) => r.type === RULE_TYPE_TRANSFORM_STEP)
    .map((r) => ({
      id: r.id,
      storeId: r.store_id,
      stepOrder: r.execution_rank ?? 0,
      enabled: r.is_active !== false,
      sql: r.generated_sql || '',
      name: r.transform_step?.name || '',
      notes: r.transform_step?.notes || '',
      source: r.transform_step?.source || '',
      glueJob: r.transform_step?.glue_job || '',
      isFinal: !!r.transform_step?.is_final,
      warnings: r.transform_step?.warnings || [],
      updatedAt: r.updated_at,
    }))
    .sort((a, b) => a.stepOrder - b.stepOrder);

  const mappingRule = rules.find((r) => r.type === RULE_TYPE_COLUMN_MAPPING);

  const sources = (Array.isArray(store?.transform_sources) ? store.transform_sources : []).map(
    (s) => ({
      id: s.id,
      storeId: s.store_id,
      name: s.name || '',
      fileName: s.file_name || '',
      shared: !!s.shared,
      isTrigger: !!s.is_trigger,
      // Absent means "use the store's delimiter", stored as NULL. Distinct from
      // the column mapping's blank, which means "auto-detect".
      delimiter: s.delimiter ?? null,
      pattern: s.pattern || '',
      source: s.source || '',
      glueJob: s.glue_job || '',
      warnings: s.warnings || [],
      updatedAt: s.updated_at,
    })
  );

  return {
    storeId: store?.store_id || '',
    merchantId: store?.merchant_id || '',
    provider: store?.provider || '',
    executionPath: store?.execution_path || '',
    executionPathReason: store?.execution_path_reason || '',
    warnings: store?.warnings || [],
    mapping: store?.column_mapping || mappingRule?.column_mapping || null,
    steps,
    sources,
  };
};

export const hasMarketplaceConfig = (normalized) =>
  !!normalized?.mapping ||
  (normalized?.steps?.length || 0) > 0 ||
  (normalized?.sources?.length || 0) > 0;

/** Zero declared inputs is the single-file majority, registered as `source`. */
export const isMultiFile = (normalized) => (normalized?.sources?.length || 0) > 0;

/** The input whose arrival runs the chain, or null when the manifest is broken. */
export const triggerSource = (normalized) =>
  (normalized?.sources || []).find((s) => s.isTrigger) || null;

/**
 * Pull a readable message out of an axios error from these endpoints.
 *
 * Handles a JSON body that arrived as a raw string, which is the normal case for
 * the transform-test call: its success body is CSV, so the response is not
 * JSON-parsed and `{"error":"…"}` reaches us verbatim.
 */
export const apiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (typeof data === 'string') {
    const raw = data.trim();
    if (!raw) return fallback;
    if (raw.startsWith('{')) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.error) return parsed.error;
        if (parsed?.message) return parsed.message;
      } catch {
        // Not JSON after all — fall through and show the body as-is.
      }
    }
    return raw;
  }

  if (data?.error) return data.error;
  if (data?.message) return data.message;
  return error?.message || fallback;
};
